import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Chip,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
} from '@mui/material';
import { deviceReplacementsApi } from '../../api/deviceReplacements';
import { meterRadioHeadsApi } from '../../api/meterRadioHeads';
import { heatApi } from '../../api/heat';

const REASONS = [
  'Неисправность',
  'Поверка/замена по сроку',
  'Повреждение',
  'Замена по требованию УК',
  'Другое',
];

const HEAT_REPLACEMENT_OPTIONS = [
  {
    value: 'SUM_OLD_NEW',
    label: 'Обычная замена: расход до и после',
    description:
      'Часть месяца по старому УРТ, часть по новому — в отчёте расходы складываются. Стандартный вариант при замене посреди месяца.',
  },
  {
    value: 'NEW_ONLY',
    label: 'Только новый УРТ',
    description:
      'В месяце замены учитывается только новый прибор с даты установки. Расход со старого до замены не суммируется.',
  },
  {
    value: 'AREA',
    label: 'Весь месяц по площади',
    description:
      'Месяц замены целиком считается по площади квартиры, без показаний УРТ. Если приборы в этом месяце нельзя корректно учесть.',
  },
];

const WATER_TYPES = new Set(['ХВС', 'ГВС']);

const buildInitialForm = (device) => ({
  scope: 'meter',
  serial_number: '',
  dev_eui: '',
  mqtt_topic: '',
  reading_collection_mode:
    device?.reading_collection_mode === 'file_import' ? 'file_import' : 'automatic',
  manufacturer: '',
  name: '',
  head_serial_number: '',
  head_dev_eui: '',
  head_manufacturer: '',
  head_model: '',
  head_baseline: device?.last_data ?? '',
  reason: '',
  comment: '',
  owner_email: '',
  changed_at: '',
  old_last_value: device?.last_data ?? '',
  new_start_value: '0',
  heat_calculation_mode: 'AREA',
  radiator_model_name: '',
  radiator_manufacturer: '',
  radiator_placement: '',
  radiator_room_label: '',
  radiator_section_count: '',
  radiator_power_watts: '',
  radiator_height_mm: '',
  radiator_width_mm: '',
  radiator_depth_mm: '',
  radiator_is_corner: false,
  radiator_heat_coefficient: device?.heat_coefficient ?? '',
  radiator_heat_reduction_coefficient: device?.heat_reduction_coefficient ?? '',
  radiator_notes: '',
});

function CurrentValueHint({ value }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mt: 0.75,
        color: 'text.secondary',
      }}
    >
      Текущее: <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{value || '—'}</Box>
    </Typography>
  );
}

export default function ReplaceDeviceDialog({ open, onClose, device, onReplaced }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [headHistory, setHeadHistory] = useState([]);
  const [form, setForm] = useState(() => buildInitialForm(device));

  const apartmentId = device?.apartment_id;
  const systemType = device?.system_type || '';
  const isHeatAllocator = String(systemType).trim().toLowerCase() === 'урт';
  const isWater = WATER_TYPES.has(String(systemType).trim());
  const scope = isWater || isHeatAllocator ? form.scope : 'meter';
  const showMeterFields = scope === 'meter' || scope === 'both';
  const showHeadFields = isWater && (scope === 'head' || scope === 'both');
  const showRadiatorFields = isHeatAllocator && (scope === 'radiator' || scope === 'both');
  const usesLoRaWan = Boolean(
    (showHeadFields && form.head_dev_eui.trim()) ||
      (showMeterFields && !showHeadFields && form.dev_eui.trim())
  );

  useEffect(() => {
    if (open) {
      setForm(buildInitialForm(device));
      setError('');
      if (apartmentId) {
        deviceReplacementsApi
          .listByApartment(String(apartmentId))
          .then(setHistory)
          .catch(() => setHistory([]));
      }
      if (device?.id && WATER_TYPES.has(String(device?.system_type || '').trim())) {
        meterRadioHeadsApi
          .getDeviceState(String(device.id))
          .then((state) => {
            setHeadHistory(state.history || []);
            if (state.active_binding?.head) {
              setForm((prev) => ({
                ...prev,
                head_dev_eui: '',
                head_baseline:
                  state.active_binding.baseline_meter_value != null
                    ? String(state.active_binding.baseline_meter_value)
                    : prev.head_baseline,
              }));
            }
          })
          .catch(() => setHeadHistory([]));
      } else {
        setHeadHistory([]);
      }
      if (device?.id && String(device?.system_type || '').trim().toLowerCase() === 'урт') {
        heatApi.getRadiator(String(device.id)).then((radiator) => {
          setForm((prev) => ({
            ...prev,
            radiator_model_name: radiator.model_name || '',
            radiator_manufacturer: radiator.manufacturer || '',
            radiator_placement: radiator.placement || '',
            radiator_room_label: radiator.room_label || '',
            radiator_section_count: radiator.section_count ?? '',
            radiator_power_watts: radiator.power_watts ?? '',
            radiator_height_mm: radiator.height_mm ?? '',
            radiator_width_mm: radiator.width_mm ?? '',
            radiator_depth_mm: radiator.depth_mm ?? '',
            radiator_is_corner: Boolean(radiator.is_corner),
            radiator_heat_coefficient: radiator.heat_coefficient ?? '',
            radiator_heat_reduction_coefficient: radiator.heat_reduction_coefficient ?? '',
            radiator_notes: radiator.notes || '',
          }));
        }).catch(() => {});
      }
    }
  }, [open, apartmentId, device]);

  const canSubmit = useMemo(() => {
    if (scope === 'radiator') {
      return Boolean(
        form.radiator_model_name.trim() ||
          form.radiator_manufacturer.trim() ||
          form.radiator_power_watts !== '' ||
          form.radiator_heat_coefficient !== ''
      );
    }
    if (scope === 'head') {
      return Boolean(form.head_dev_eui.trim());
    }
    if (scope === 'both') {
      if (isWater) {
        return Boolean(form.serial_number.trim() && form.head_dev_eui.trim());
      }
      return Boolean(
        form.serial_number.trim() &&
          (form.radiator_model_name.trim() ||
            form.radiator_manufacturer.trim() ||
            form.radiator_power_watts !== '' ||
            form.radiator_heat_coefficient !== '')
      );
    }
    return Boolean(form.serial_number.trim());
  }, [form, isWater, scope]);

  const handleSubmit = async () => {
    if (!device?.id || !canSubmit || loading) return;
    try {
      setLoading(true);
      setError('');
      const toNumberOrNull = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const num = Number(String(value).replace(',', '.'));
        return Number.isFinite(num) ? num : null;
      };
      const oldLastValue = toNumberOrNull(form.old_last_value);
      const newStartValue = toNumberOrNull(form.new_start_value);
      if (form.old_last_value !== '' && (oldLastValue === null || oldLastValue < 0)) {
        setError('Последнее значение старого прибора должно быть числом не меньше нуля.');
        return;
      }
      if (showMeterFields && (newStartValue === null || newStartValue < 0)) {
        setError('Укажите стартовое значение нового прибора.');
        return;
      }

      const payload = {
        replacement_scope: scope,
        changed_at: form.changed_at ? new Date(form.changed_at).toISOString() : null,
        old_last_value: oldLastValue,
        new_start_value: newStartValue,
        head_baseline_meter_value: toNumberOrNull(form.head_baseline),
        heat_calculation_mode: isHeatAllocator ? form.heat_calculation_mode : 'SUM_OLD_NEW',
        reason: form.reason || null,
        comment: form.comment.trim() || null,
        owner_email: form.owner_email.trim() || null,
      };

      if (showMeterFields) {
        payload.new_device = {
          serial_number: form.serial_number.trim(),
          // For meter-only water: DevEUI comes from transferred head; allow override for non-water.
          dev_eui: showHeadFields ? null : form.dev_eui.trim() || null,
          mqtt_topic: form.mqtt_topic.trim() || null,
          manufacturer: form.manufacturer.trim() || null,
          name: form.name.trim() || null,
          system_type: systemType || null,
          description: null,
          reading_collection_mode: form.reading_collection_mode,
        };
      }

      if (showHeadFields) {
        payload.new_head = {
          serial_number: form.head_serial_number.trim() || null,
          dev_eui: form.head_dev_eui.trim(),
          manufacturer: form.head_manufacturer.trim() || null,
          model: form.head_model.trim() || null,
          notes: form.comment.trim() || null,
        };
      }

      if (showRadiatorFields) {
        payload.new_radiator = {
          model_name: form.radiator_model_name.trim() || null,
          manufacturer: form.radiator_manufacturer.trim() || null,
          placement: form.radiator_placement.trim() || null,
          room_label: form.radiator_room_label.trim() || null,
          section_count: toNumberOrNull(form.radiator_section_count),
          power_watts: toNumberOrNull(form.radiator_power_watts),
          height_mm: toNumberOrNull(form.radiator_height_mm),
          width_mm: toNumberOrNull(form.radiator_width_mm),
          depth_mm: toNumberOrNull(form.radiator_depth_mm),
          is_corner: Boolean(form.radiator_is_corner),
          heat_coefficient: toNumberOrNull(form.radiator_heat_coefficient),
          heat_reduction_coefficient: toNumberOrNull(
            form.radiator_heat_reduction_coefficient
          ),
          notes: form.radiator_notes.trim() || null,
        };
      }

      await deviceReplacementsApi.replaceDevice(String(device.id), payload);
      onReplaced?.();
      onClose?.();
    } catch (err) {
      console.error('replace device failed', err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Не удалось заменить прибор. Проверьте данные и попробуйте снова.'
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU');
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isHeatAllocator ? 'Замена УРТ или радиатора' : 'Замена прибора учёта'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box>
            <Typography variant="subtitle2">Текущий прибор</Typography>
            <Typography variant="body2">
              Серийный: {device?.serial_number || '—'} · Тип: {systemType || '—'}
              {device?.dev_eui ? ` · DevEUI: ${device.dev_eui}` : ''}
            </Typography>
          </Box>

          {isWater ? (
            <>
              <Divider />
              <FormControl>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Что меняем?
                </Typography>
                <RadioGroup
                  value={form.scope}
                  onChange={(event) => setForm((state) => ({ ...state, scope: event.target.value }))}
                >
                  <FormControlLabel
                    value="meter"
                    control={<Radio size="small" />}
                    label="Только счётчик (накладка переносится)"
                  />
                  <FormControlLabel
                    value="head"
                    control={<Radio size="small" />}
                    label="Только накладку (серийник счётчика без изменений)"
                  />
                  <FormControlLabel
                    value="both"
                    control={<Radio size="small" />}
                    label="Счётчик и накладку"
                  />
                </RadioGroup>
              </FormControl>
            </>
          ) : null}

          {isHeatAllocator ? (
            <>
              <Divider />
              <FormControl>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Что меняем?</Typography>
                <RadioGroup
                  value={form.scope}
                  onChange={(event) => setForm((state) => ({ ...state, scope: event.target.value }))}
                >
                  <FormControlLabel value="meter" control={<Radio size="small" />} label="Только УРТ" />
                  <FormControlLabel value="radiator" control={<Radio size="small" />} label="Только радиатор" />
                  <FormControlLabel value="both" control={<Radio size="small" />} label="УРТ и радиатор" />
                </RadioGroup>
              </FormControl>
            </>
          ) : null}

          {showMeterFields ? (
            <>
              <Divider />
              <Typography variant="subtitle2">Новый прибор</Typography>
              <TextField
                select
                label="Режим сбора"
                value={form.reading_collection_mode}
                onChange={(e) =>
                  setForm((s) => ({ ...s, reading_collection_mode: e.target.value }))
                }
                fullWidth
              >
                <MenuItem value="file_import">Импорт из файла</MenuItem>
                <MenuItem value="automatic">Автоматический сбор</MenuItem>
              </TextField>
              <Box>
                <TextField
                  label="Серийный номер"
                  value={form.serial_number}
                  onChange={(e) => setForm((s) => ({ ...s, serial_number: e.target.value }))}
                  required
                  fullWidth
                />
                <CurrentValueHint value={device?.serial_number} />
              </Box>
              {form.reading_collection_mode === 'automatic' && !isWater ? (
                <>
                  <Box>
                    <TextField
                      label="DevEUI"
                      value={form.dev_eui}
                      onChange={(e) => setForm((s) => ({ ...s, dev_eui: e.target.value }))}
                      placeholder="например: 0018AF0000000001"
                      fullWidth
                    />
                    <CurrentValueHint value={device?.dev_eui} />
                  </Box>
                  <Box>
                    <TextField
                      label="MQTT-топик"
                      value={form.mqtt_topic}
                      onChange={(e) => setForm((s) => ({ ...s, mqtt_topic: e.target.value }))}
                      fullWidth
                    />
                    <CurrentValueHint value={device?.mqtt_topic} />
                  </Box>
                </>
              ) : null}
              {isWater && scope === 'meter' ? (
                <Alert severity="info">
                  DevEUI останется от текущей накладки и будет перенесён на новый счётчик.
                </Alert>
              ) : null}
              <Box>
                <TextField
                  label="Производитель"
                  value={form.manufacturer}
                  onChange={(e) => setForm((s) => ({ ...s, manufacturer: e.target.value }))}
                  fullWidth
                />
                <CurrentValueHint value={device?.manufacturer} />
              </Box>
              <Box>
                <TextField
                  label="Название"
                  value={form.name}
                  onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                  fullWidth
                />
                <CurrentValueHint value={device?.name} />
              </Box>
            </>
          ) : null}

          {showHeadFields ? (
            <>
              <Divider />
              <Typography variant="subtitle2">Новая накладка</Typography>
              <TextField
                label="DevEUI накладки"
                value={form.head_dev_eui}
                onChange={(e) => setForm((s) => ({ ...s, head_dev_eui: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Серийный номер накладки"
                value={form.head_serial_number}
                onChange={(e) => setForm((s) => ({ ...s, head_serial_number: e.target.value }))}
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Производитель накладки"
                  value={form.head_manufacturer}
                  onChange={(e) => setForm((s) => ({ ...s, head_manufacturer: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Модель накладки"
                  value={form.head_model}
                  onChange={(e) => setForm((s) => ({ ...s, head_model: e.target.value }))}
                  fullWidth
                />
              </Stack>
              <TextField
                label="Начальное значение счётчика при установке накладки"
                value={form.head_baseline}
                onChange={(e) => setForm((s) => ({ ...s, head_baseline: e.target.value }))}
                fullWidth
                helperText={
                  scope === 'head'
                    ? 'Серийный номер счётчика не меняется'
                    : 'Обычно совпадает со стартовым показанием нового счётчика'
                }
              />
            </>
          ) : null}

          {showRadiatorFields ? (
            <>
              <Divider />
              <Typography variant="subtitle2">Новый радиатор</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Модель" value={form.radiator_model_name} onChange={(e) => setForm((s) => ({ ...s, radiator_model_name: e.target.value }))} fullWidth />
                <TextField label="Производитель" value={form.radiator_manufacturer} onChange={(e) => setForm((s) => ({ ...s, radiator_manufacturer: e.target.value }))} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Расположение" value={form.radiator_placement} onChange={(e) => setForm((s) => ({ ...s, radiator_placement: e.target.value }))} fullWidth />
                <TextField label="Помещение" value={form.radiator_room_label} onChange={(e) => setForm((s) => ({ ...s, radiator_room_label: e.target.value }))} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Секций" type="number" value={form.radiator_section_count} onChange={(e) => setForm((s) => ({ ...s, radiator_section_count: e.target.value }))} fullWidth />
                <TextField label="Мощность, Вт" type="number" value={form.radiator_power_watts} onChange={(e) => setForm((s) => ({ ...s, radiator_power_watts: e.target.value }))} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Расчётный коэффициент" type="number" value={form.radiator_heat_coefficient} onChange={(e) => setForm((s) => ({ ...s, radiator_heat_coefficient: e.target.value }))} fullWidth />
                <TextField label="Понижающий коэффициент" type="number" value={form.radiator_heat_reduction_coefficient} onChange={(e) => setForm((s) => ({ ...s, radiator_heat_reduction_coefficient: e.target.value }))} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Высота, мм" type="number" value={form.radiator_height_mm} onChange={(e) => setForm((s) => ({ ...s, radiator_height_mm: e.target.value }))} fullWidth />
                <TextField label="Ширина, мм" type="number" value={form.radiator_width_mm} onChange={(e) => setForm((s) => ({ ...s, radiator_width_mm: e.target.value }))} fullWidth />
                <TextField label="Глубина, мм" type="number" value={form.radiator_depth_mm} onChange={(e) => setForm((s) => ({ ...s, radiator_depth_mm: e.target.value }))} fullWidth />
              </Stack>
              <FormControlLabel
                control={<Checkbox checked={form.radiator_is_corner} onChange={(e) => setForm((s) => ({ ...s, radiator_is_corner: e.target.checked }))} />}
                label="Угловой радиатор"
              />
              <TextField label="Примечание к радиатору" value={form.radiator_notes} onChange={(e) => setForm((s) => ({ ...s, radiator_notes: e.target.value }))} multiline minRows={2} />
            </>
          ) : null}

          <Divider />

          <Typography variant="subtitle2">Данные замены</Typography>
          <TextField
            label="Дата замены"
            type="datetime-local"
            value={form.changed_at}
            onChange={(e) => setForm((s) => ({ ...s, changed_at: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          {isHeatAllocator && showMeterFields ? (
            <FormControl>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Как считать тепло в месяце замены
              </Typography>
              <RadioGroup
                value={form.heat_calculation_mode}
                onChange={(event) => setForm((state) => ({
                  ...state,
                  heat_calculation_mode: event.target.value,
                }))}
              >
                {HEAT_REPLACEMENT_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" />}
                    sx={{ alignItems: 'flex-start', m: 0, py: 0.25 }}
                    label={(
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    )}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          ) : null}
          {showRadiatorFields && !showMeterFields ? (
            <>
              <Box>
                <TextField
                  label="Показание УРТ при замене радиатора"
                  value={form.old_last_value}
                  onChange={(e) => setForm((s) => ({ ...s, old_last_value: e.target.value }))}
                  fullWidth
                />
                <CurrentValueHint value={device?.last_data} />
              </Box>
              <Alert severity="info">Месяц замены радиатора рассчитывается по площади.</Alert>
            </>
          ) : null}
          {showMeterFields ? (
            <>
              <Box>
                <TextField
                  label="Последнее значение старого прибора"
                  value={form.old_last_value}
                  onChange={(e) => setForm((s) => ({ ...s, old_last_value: e.target.value }))}
                  fullWidth
                />
                <CurrentValueHint value={device?.last_data} />
              </Box>
              <TextField
                label="Стартовое значение нового прибора"
                value={form.new_start_value}
                onChange={(e) => setForm((s) => ({ ...s, new_start_value: e.target.value }))}
                fullWidth
                required
                helperText="По умолчанию 0; укажите фактическое значение при установке"
              />
            </>
          ) : showHeadFields ? (
            <Box>
              <TextField
                label="Текущее показание счётчика"
                value={form.old_last_value}
                onChange={(e) => setForm((s) => ({
                  ...s,
                  old_last_value: e.target.value,
                  head_baseline: e.target.value,
                }))}
                fullWidth
                helperText="Фиксируется как начальное для новой накладки"
              />
              <CurrentValueHint value={device?.last_data} />
            </Box>
          ) : null}
          <TextField
            select
            label="Причина"
            value={form.reason}
            onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))}
            SelectProps={{ native: true }}
          >
            <option value="">—</option>
            {REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </TextField>
          <TextField
            label="Комментарий"
            multiline
            minRows={2}
            value={form.comment}
            onChange={(e) => setForm((s) => ({ ...s, comment: e.target.value }))}
          />
          <TextField
            label="E-mail владельца для акта"
            type="email"
            value={form.owner_email}
            onChange={(e) => setForm((s) => ({ ...s, owner_email: e.target.value }))}
            helperText="Сохраняется с заменой; в мобильном приложении можно отправить PDF-акты на эту почту"
          />

          {usesLoRaWan && (
            <Alert severity="warning">
              Указан DevEUI. После замены проверьте регистрацию в BillingService LoRaWAN.
            </Alert>
          )}

          {history.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">История замен приборов</Typography>
              <List dense sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }}>
                {history.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    <ListItem>
                      <ListItemText
                        primary={`Дата: ${formatDate(item.changed_at)}`}
                        secondary={
                          item.reason || item.comment
                            ? `${item.reason || ''}${item.comment ? ` · ${item.comment}` : ''}`
                            : '—'
                        }
                      />
                      <Chip
                        size="small"
                        label={
                          item.replacement_scope === 'radiator'
                            ? 'Радиатор'
                            : item.new_device_serial || '—'
                        }
                      />
                    </ListItem>
                    {idx < history.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}

          {headHistory.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2">История накладок</Typography>
              <List dense sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1 }}>
                {headHistory.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    <ListItem>
                      <ListItemText
                        primary={`${item.head?.dev_eui || item.head_id}${item.active_to ? ' · закрыта' : ' · активна'}`}
                        secondary={`с ${formatDate(item.active_from)}${
                          item.active_to ? ` по ${formatDate(item.active_to)}` : ''
                        }${
                          item.baseline_meter_value != null
                            ? ` · начальное ${item.baseline_meter_value}`
                            : ''
                        }`}
                      />
                    </ListItem>
                    {idx < headHistory.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Отмена
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || loading}>
          {scope === 'head' ? 'Заменить накладку' : scope === 'radiator' ? 'Заменить радиатор' : 'Заменить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
